import React, { useState, useEffect } from 'react';
import Loading from '../common/Loading';

const StoresPage = () => {
  const [loading, setLoading] = useState(true);
  const [stores, setStores] = useState([]);
  const [editingStore, setEditingStore] = useState(null);
  const [showStoreModal, setShowStoreModal] = useState(false);
  const [filter, setFilter] = useState('');

  // コンポーネントマウント時にデータを取得
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // 実際の実装ではAPIコールを行う
        // 今回はモックデータでシミュレーション
        const mockStores = generateMockStores();
        setStores(mockStores);
        
        setLoading(false);
      } catch (error) {
        console.error('データ取得エラー:', error);
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);

  // モック店舗データを生成
  const generateMockStores = () => {
    return [
      {
        id: 1,
        name: '本店',
        address: '東京都渋谷区渋谷1-1-1',
        phone: '03-1234-5678',
        businessHours: '9:00-22:00',
        isActive: true,
        manager: '山田太郎',
        createdAt: '2023-01-15'
      },
      {
        id: 2,
        name: '西口店',
        address: '東京都新宿区西新宿2-2-2',
        phone: '03-2345-6789',
        businessHours: '10:00-21:00',
        isActive: true,
        manager: '佐藤次郎',
        createdAt: '2023-03-20'
      },
      {
        id: 3,
        name: '駅前店',
        address: '東京都品川区大崎3-3-3',
        phone: '03-3456-7890',
        businessHours: '8:00-20:00',
        isActive: true,
        manager: '田中三郎',
        createdAt: '2023-05-10'
      }
    ];
  };

  // 新規店舗追加ボタンのクリックハンドラ
  const handleAddStore = () => {
    setEditingStore({
      id: null,
      name: '',
      address: '',
      phone: '',
      businessHours: '',
      isActive: true,
      manager: '',
      createdAt: new Date().toISOString().split('T')[0]
    });
    setShowStoreModal(true);
  };

  // 店舗編集ボタンのクリックハンドラ
  const handleEditStore = (store) => {
    setEditingStore(store);
    setShowStoreModal(true);
  };

  // 店舗削除ボタンのクリックハンドラ
  const handleDeleteStore = (storeId) => {
    if (window.confirm('この店舗を削除してもよろしいですか？')) {
      // 実際の実装ではAPIコールを行う
      setStores(prevStores => prevStores.filter(store => store.id !== storeId));
    }
  };

  // 店舗保存ハンドラ
  const handleSaveStore = (updatedStore) => {
    if (updatedStore.id) {
      // 既存店舗の更新
      setStores(prevStores => 
        prevStores.map(store => 
          store.id === updatedStore.id ? updatedStore : store
        )
      );
    } else {
      // 新規店舗の追加
      const newStore = {
        ...updatedStore,
        id: Math.max(...stores.map(s => s.id), 0) + 1,
        createdAt: new Date().toISOString().split('T')[0]
      };
      setStores(prevStores => [...prevStores, newStore]);
    }
    
    setShowStoreModal(false);
    setEditingStore(null);
  };

  // フィルタリングされた店舗リスト
  const filteredStores = stores.filter(store => 
    store.name.toLowerCase().includes(filter.toLowerCase()) ||
    store.address.toLowerCase().includes(filter.toLowerCase()) ||
    store.manager.toLowerCase().includes(filter.toLowerCase())
  );

  // 店舗モーダル
  const StoreModal = () => {
    const [storeData, setStoreData] = useState(editingStore);
    
    const handleInputChange = (e) => {
      const { name, value, type, checked } = e.target;
      setStoreData({
        ...storeData,
        [name]: type === 'checkbox' ? checked : value
      });
    };
    
    const handleSubmit = (e) => {
      e.preventDefault();
      handleSaveStore(storeData);
    };
    
    return (
      <div className="modal-overlay">
        <div className="modal-content">
          <div className="modal-header">
            <h3>{editingStore.id ? '店舗を編集' : '新規店舗を追加'}</h3>
            <button 
              className="modal-close-btn"
              onClick={() => setShowStoreModal(false)}
            >
              ×
            </button>
          </div>
          
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="name">店舗名 <span className="required">*</span></label>
              <input 
                type="text" 
                id="name" 
                name="name"
                value={storeData.name}
                onChange={handleInputChange}
                required
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="address">住所 <span className="required">*</span></label>
              <input 
                type="text" 
                id="address" 
                name="address"
                value={storeData.address}
                onChange={handleInputChange}
                required
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="phone">電話番号 <span className="required">*</span></label>
              <input 
                type="tel" 
                id="phone" 
                name="phone"
                value={storeData.phone}
                onChange={handleInputChange}
                required
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="businessHours">営業時間 <span className="required">*</span></label>
              <input 
                type="text" 
                id="businessHours" 
                name="businessHours"
                value={storeData.businessHours}
                onChange={handleInputChange}
                required
                placeholder="例: 9:00-22:00"
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="manager">店長名 <span className="required">*</span></label>
              <input 
                type="text" 
                id="manager" 
                name="manager"
                value={storeData.manager}
                onChange={handleInputChange}
                required
              />
            </div>
            
            <div className="form-group checkbox-group">
              <label>
                <input 
                  type="checkbox" 
                  name="isActive"
                  checked={storeData.isActive}
                  onChange={handleInputChange}
                />
                アクティブ（営業中）
              </label>
            </div>
            
            <div className="form-actions">
              <button type="submit" className="save-btn">保存</button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  if (loading) {
    return <Loading />;
  }

  return (
    <div className="stores-page">
      <h2>店舗管理</h2>
      <p>店舗情報の登録、編集、削除ができます</p>
      
      <div className="action-bar">
        <div className="search-box">
          <input 
            type="text"
            placeholder="店舗名、住所、店長名で検索..."
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
        </div>
        
        <button 
          className="add-store-btn"
          onClick={handleAddStore}
        >
          新規店舗を追加
        </button>
      </div>
      
      <div className="stores-list">
        <table className="stores-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>店舗名</th>
              <th>住所</th>
              <th>電話番号</th>
              <th>営業時間</th>
              <th>店長</th>
              <th>ステータス</th>
              <th>作成日</th>
              <th>操作</th>
            </tr>
          </thead>
          <tbody>
            {filteredStores.length > 0 ? (
              filteredStores.map(store => (
                <tr key={store.id}>
                  <td>{store.id}</td>
                  <td>{store.name}</td>
                  <td>{store.address}</td>
                  <td>{store.phone}</td>
                  <td>{store.businessHours}</td>
                  <td>{store.manager}</td>
                  <td>
                    <span className={`status-badge ${store.isActive ? 'active' : 'inactive'}`}>
                      {store.isActive ? '営業中' : '休業中'}
                    </span>
                  </td>
                  <td>{store.createdAt}</td>
                  <td>
                    <div className="action-buttons">
                      <button 
                        className="edit-btn"
                        onClick={() => handleEditStore(store)}
                      >
                        編集
                      </button>
                      <button 
                        className="delete-btn"
                        onClick={() => handleDeleteStore(store.id)}
                      >
                        削除
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="9" className="no-results">該当する店舗がありません</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      
      {showStoreModal && <StoreModal />}
    </div>
  );
};

export default StoresPage;
